import {
    AppleOutlined,
    CopyOutlined,
    CheckOutlined,
    WindowsOutlined,
    FileTextOutlined,
} from '@ant-design/icons';
import { useState, useMemo } from 'react';
import { UAParser } from 'ua-parser-js';
import Styles from '../../Styles.module.scss';
import cs from 'classnames';
import { getBrandBaseUrl, isSelfHosted } from '../../../../common/utils/getBrandDomain';

const GITHUB_REPO = 'model-park/cli';
const INSTALL_SCRIPT_URL = `https://raw.githubusercontent.com/${GITHUB_REPO}/master/install.sh`;
const LATEST_DOWNLOAD_BASE = `https://github.com/${GITHUB_REPO}/releases/latest/download`;

type Platform = 'macos' | 'linux' | 'windows';

interface PlatformConfig {
    id: Platform;
    label: string;
    icon: React.ReactNode;
}

const platforms: PlatformConfig[] = [
    { id: 'macos', label: 'macOS', icon: <AppleOutlined /> },
    { id: 'linux', label: 'Linux', icon: <span>🐧</span> },
    { id: 'windows', label: 'Windows', icon: <WindowsOutlined /> },
];

function detectPlatform(): Platform {
    const parser = new UAParser();
    const osName = parser.getOS().name;

    if (!osName) return 'linux';
    if (osName.includes('Windows')) return 'windows';
    if (osName.includes('Mac')) return 'macos';
    return 'linux';
}

function CodeBlock({ code, onCopy }: { code: string; onCopy: () => void }) {
    const [copied, setCopied] = useState(false);

    const handleCopy = async () => {
        await navigator.clipboard.writeText(code);
        setCopied(true);
        onCopy();
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <div className={Styles.CodeBlock}>
            <code>{code}</code>
            <button className={Styles.CopyButton} onClick={handleCopy}>
                {copied ? <CheckOutlined /> : <CopyOutlined />} {copied ? 'Copied' : 'Copy'}
            </button>
        </div>
    );
}

export function Installation() {
    const detectedPlatform = useMemo(() => detectPlatform(), []);
    const [activePlatform, setActivePlatform] = useState<Platform>(detectedPlatform);

    // On a self-hosted deployment the CLI must be pointed at this server,
    // otherwise it silently defaults to the public modelpark.app API.
    const brandBaseUrl = getBrandBaseUrl();
    const selfHosted = isSelfHosted();
    const installerCommand = selfHosted
        ? `curl -fsSL ${INSTALL_SCRIPT_URL} | MODELPARK_SERVER=${brandBaseUrl} sh`
        : `curl -fsSL ${INSTALL_SCRIPT_URL} | sh`;
    const loginCommand = selfHosted
        ? `modelpark login --server ${brandBaseUrl}`
        : 'modelpark login';

    return (
        <div id="installation" className={Styles.Section}>
            <h2 className={Styles.SectionTitle}>Installation</h2>
            <p className={Styles.SectionDescription}>
                Install the ModelPark CLI to expose your local ML applications to the internet.
            </p>

            <div className={Styles.PlatformTabs}>
                {platforms.map(platform => (
                    <button
                        key={platform.id}
                        className={cs(Styles.PlatformTab, {
                            [Styles.active]: activePlatform === platform.id,
                        })}
                        onClick={() => setActivePlatform(platform.id)}
                    >
                        <span className={Styles.PlatformIcon}>{platform.icon}</span>
                        {platform.label}
                    </button>
                ))}
            </div>

            {activePlatform === 'macos' && (
                <div className={Styles.InstallCard}>
                    <div className={Styles.InstallCardTitle}>
                        <AppleOutlined /> macOS Installation
                    </div>

                    <div className={Styles.InstallSteps}>
                        <div className={Styles.InstallStep}>
                            <div className={Styles.StepNumber}>1</div>
                            <div className={Styles.StepContent}>
                                <div className={Styles.StepLabel}>
                                    Run the installer script (Apple Silicon & Intel)
                                </div>
                                <CodeBlock code={installerCommand} onCopy={() => {}} />
                            </div>
                        </div>

                        <div className={Styles.InstallStep}>
                            <div className={Styles.StepNumber}>2</div>
                            <div className={Styles.StepContent}>
                                <div className={Styles.StepLabel}>Verify installation</div>
                                <CodeBlock code="modelpark version" onCopy={() => {}} />
                            </div>
                        </div>

                        <div className={Styles.InstallStep}>
                            <div className={Styles.StepNumber}>3</div>
                            <div className={Styles.StepContent}>
                                <div className={Styles.StepLabel}>Sign in</div>
                                <CodeBlock code={loginCommand} onCopy={() => {}} />
                            </div>
                        </div>
                    </div>

                    <div style={{ marginTop: 24 }}>
                        <div className={Styles.StepLabel} style={{ marginBottom: 8 }}>
                            Or download manually:
                        </div>
                        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                            <a
                                href={`${LATEST_DOWNLOAD_BASE}/modelpark-darwin-arm64.tar.gz`}
                                className={Styles.ManualDownloadLink}
                                style={{ margin: 0 }}
                            >
                                Apple Silicon (M1/M2/M3)
                            </a>
                            <a
                                href={`${LATEST_DOWNLOAD_BASE}/modelpark-darwin-amd64.tar.gz`}
                                className={Styles.ManualDownloadLink}
                                style={{ margin: 0 }}
                            >
                                Intel
                            </a>
                        </div>
                    </div>
                </div>
            )}

            {activePlatform === 'linux' && (
                <div className={Styles.InstallCard}>
                    <div className={Styles.InstallCardTitle}>
                        <span>🐧</span> Linux Installation
                    </div>

                    <div className={Styles.InstallSteps}>
                        <div className={Styles.InstallStep}>
                            <div className={Styles.StepNumber}>1</div>
                            <div className={Styles.StepContent}>
                                <div className={Styles.StepLabel}>
                                    Run the installer script (auto-detects architecture)
                                </div>
                                <CodeBlock code={installerCommand} onCopy={() => {}} />
                            </div>
                        </div>

                        <div className={Styles.InstallStep}>
                            <div className={Styles.StepNumber}>2</div>
                            <div className={Styles.StepContent}>
                                <div className={Styles.StepLabel}>Verify installation</div>
                                <CodeBlock code="modelpark version" onCopy={() => {}} />
                            </div>
                        </div>

                        <div className={Styles.InstallStep}>
                            <div className={Styles.StepNumber}>3</div>
                            <div className={Styles.StepContent}>
                                <div className={Styles.StepLabel}>Sign in</div>
                                <CodeBlock code={loginCommand} onCopy={() => {}} />
                            </div>
                        </div>
                    </div>

                    <div style={{ marginTop: 24 }}>
                        <div className={Styles.StepLabel} style={{ marginBottom: 8 }}>
                            Or download manually:
                        </div>
                        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                            <a
                                href={`${LATEST_DOWNLOAD_BASE}/modelpark-linux-amd64.tar.gz`}
                                className={Styles.ManualDownloadLink}
                                style={{ margin: 0 }}
                            >
                                x86_64 (amd64)
                            </a>
                            <a
                                href={`${LATEST_DOWNLOAD_BASE}/modelpark-linux-arm64.tar.gz`}
                                className={Styles.ManualDownloadLink}
                                style={{ margin: 0 }}
                            >
                                ARM64
                            </a>
                        </div>
                    </div>
                </div>
            )}

            {activePlatform === 'windows' && (
                <div className={Styles.InstallCard}>
                    <div className={Styles.InstallCardTitle}>
                        <WindowsOutlined /> Windows Installation
                    </div>

                    <div className={Styles.InstallSteps}>
                        <div className={Styles.InstallStep}>
                            <div className={Styles.StepNumber}>1</div>
                            <div className={Styles.StepContent}>
                                <div className={Styles.StepLabel}>
                                    Download and install using PowerShell
                                </div>
                                <CodeBlock
                                    code={`New-Item -Path "$env:LOCALAPPDATA\\Programs\\modelpark" -ItemType Directory -Force | Out-Null; Invoke-WebRequest -Uri "${LATEST_DOWNLOAD_BASE}/modelpark-windows-amd64.zip" -OutFile "$env:TEMP\\modelpark.zip"; Expand-Archive "$env:TEMP\\modelpark.zip" -DestinationPath "$env:LOCALAPPDATA\\Programs\\modelpark" -Force; Remove-Item "$env:TEMP\\modelpark.zip"`}
                                    onCopy={() => {}}
                                />
                            </div>
                        </div>

                        <div className={Styles.InstallStep}>
                            <div className={Styles.StepNumber}>2</div>
                            <div className={Styles.StepContent}>
                                <div className={Styles.StepLabel}>
                                    Add to PATH (run once)
                                </div>
                                <CodeBlock
                                    code={'$path = "$env:LOCALAPPDATA\\Programs\\modelpark"; if ($env:PATH -notlike "*$path*") { [Environment]::SetEnvironmentVariable("PATH", "$env:PATH;$path", "User") }'}
                                    onCopy={() => {}}
                                />
                            </div>
                        </div>

                        <div className={Styles.InstallStep}>
                            <div className={Styles.StepNumber}>3</div>
                            <div className={Styles.StepContent}>
                                <div className={Styles.StepLabel}>Restart your terminal, then verify</div>
                                <CodeBlock code="modelpark version" onCopy={() => {}} />
                            </div>
                        </div>
                    </div>

                    <div style={{ marginTop: 24 }}>
                        <a
                            href={`${LATEST_DOWNLOAD_BASE}/modelpark-windows-amd64.zip`}
                            className={Styles.ManualDownloadLink}
                            style={{ margin: 0 }}
                        >
                            Direct Download (Windows x64)
                        </a>
                    </div>
                </div>
            )}

            <a
                href={`https://github.com/${GITHUB_REPO}/releases`}
                target="_blank"
                rel="noopener noreferrer"
                className={Styles.ManualDownloadLink}
            >
                <FileTextOutlined /> View all releases
            </a>
        </div>
    );
}
